import { getBuyingMarkerInformations } from 'api/buying';
import Popup from 'components/Popup';
import {
  DEFAULT_LATITUDE,
  DEFAULT_LONGITUDE,
  DEFAULT_ZOOM,
} from 'constants/map';
import { FunctionComponent, useEffect, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store/rootReducer';
import styled from 'styled-components';
import {
  BuyingComplex,
  BuyingFilter,
  BuyingMapFilter,
  BuyingMarker,
} from 'types/buying';
import { Nullable } from 'types/common';
import { Message, sendReactNativeMessage } from 'utils/ReactNativeMessage';
import BuyingMapMarker from './BuyingMapMarker';

const WEBVIEW_API = {
  CHANGE_BUYING_FILTER: 'CHANGE_BUYING_FILTER',
  FOCUS_OUT_MARKER: 'FOCUS_OUT_MARKER',
} as const;
type WEBVIEW_API = typeof WEBVIEW_API[keyof typeof WEBVIEW_API];

const BuyingMap: FunctionComponent = () => {
  const map = useRef<naver.maps.Map>();
  const { bldInfo } = useSelector((state: RootState) => state.mapStore);
  const [markerArray, setMarkerArray] = useState<BuyingMarker[]>([]);
  const [bottomSheetVisible, setBottomSheetVisible] = useState(false);
  const [selectedComplex, setSelectedComplex] =
    useState<Nullable<BuyingComplex>>(null);
  const initialBuyingFilter = {
    tradePrices: [0, 24],
    depositPrices: [0, 16],
    rentalDepositPrices: [0, 8],
    monthlyPrices: [0, 200],
    pyungs: [0, 80],
    tradeTypes: ['TRADE', 'RENTAL', 'MONTHLY_RENTAL'],
  };
  const savedBuyingFilter = window.localStorage.getItem('buyingFilter');
  const buyingFilter = savedBuyingFilter
    ? JSON.parse(savedBuyingFilter)
    : initialBuyingFilter;

  const [filteredData, setFilteredData] = useState<BuyingFilter>(buyingFilter);

  useEffect(() => {
    initWebViewMessageListener();

    drawMap();
  }, []);

  useEffect(() => {
    if (!map.current) return () => {};

    drawMarker(map.current, filteredData);

    const mapIdleEvent = map.current.addListener('idle', () => {
      if (map.current) {
        saveMapPositionToLocalStorage(map.current);

        drawMarker(map.current, filteredData);
      }
    });

    const mapClickEvent = map.current.addListener('click', () => {
      closeBottomSheet();
      setSelectedComplex(null);
    });

    return () => {
      if (map.current) {
        map.current.removeListener(mapClickEvent);
        map.current.removeListener(mapIdleEvent);
      }
    };
  }, [filteredData]);

  useEffect(() => {
    if (bldInfo && map.current) {
      const newCenter = new naver.maps.LatLng(
        bldInfo?.complexLatCenter,
        bldInfo?.complexLonCenter,
      );

      map.current.morph(newCenter, 17);
    }
  }, [bldInfo]);

  const handleFilteredData = (item: BuyingFilter) => {
    window.localStorage.setItem('buyingFilter', JSON.stringify(item));
    setFilteredData(item);
    closeBottomSheet();
  };

  const saveMapPositionToLocalStorage = (mapObject: naver.maps.Map) => {
    const { x, y } = mapObject.getCenter();
    const zoom = mapObject.getZoom();

    window.localStorage.setItem('buyingZoom', `${zoom}`);
    window.localStorage.setItem('buyingCenterY', `${y}`);
    window.localStorage.setItem('buyingCenterX', `${x}`);
  };

  const initWebViewMessageListener = () => {
    if (!isMobile) {
      return;
    }

    if (!window.ReactNativeWebView) {
      return;
    }

    const webviewListener = (event: any) => {
      const { payload, type } = JSON.parse(event.data);
      switch (type) {
        case WEBVIEW_API.CHANGE_BUYING_FILTER:
          handleFilteredData(JSON.parse(payload));

          break;
        case WEBVIEW_API.FOCUS_OUT_MARKER:
          setSelectedComplex(null);
          break;
        default:
          break;
      }
    };

    document.addEventListener('message', webviewListener);
    window.addEventListener('message', webviewListener);
  };

  async function drawMap() {
    const zoomLevel = Number(window.localStorage.getItem('buyingZoom'));
    const centerY = Number(window.localStorage.getItem('buyingCenterY'));
    const centerX = Number(window.localStorage.getItem('buyingCenterX'));

    map.current = new naver.maps.Map('map', {
      zoom: zoomLevel || DEFAULT_ZOOM,
      center: new naver.maps.LatLng(
        centerY || DEFAULT_LATITUDE,
        centerX || DEFAULT_LONGITUDE,
      ),
    });
  }

  const drawMarker = async (map: naver.maps.Map, filter: BuyingFilter) => {
    const zoom = map.getZoom();
    const { x: longitude, y: latitude } = map.getCenter();
    const bounds = map.getBounds();
    const { x: west, y: south } = bounds.getMin();
    const { x: east, y: north } = bounds.getMax();
    const mapData: BuyingMapFilter = {
      zoom,
      latitude,
      longitude,
      west,
      north,
      east,
      south,
      pyungMin: filter.pyungs[0],
      pyungMax: filter.pyungs[1],
      depositPriceMin: filter.depositPrices[0] * 10000,
      depositPriceMax: filter.depositPrices[1] * 10000,
      rentalDepositPriceMin: filter.rentalDepositPrices[0] * 10000,
      rentalDepositPriceMax: filter.rentalDepositPrices[1] * 10000,
      monthlyPriceMin: filter.monthlyPrices[0],
      monthlyPriceMax: filter.monthlyPrices[1],
      tradePriceMin: filter.tradePrices[0] * 10000,
      tradePriceMax: filter.tradePrices[1] * 10000,
      tradeTypes: filter.tradeTypes,
    };

    const { data, error } = await getBuyingMarkerInformations(mapData);

    if (error) {
      return;
    }

    if (data) {
      setMarkerArray(data);
    }

    if (zoom < 15) {
      closeBottomSheet();
    }
  };

  const openBottomSheet = (markerInfo: BuyingComplex) => {
    setBottomSheetVisible(true);
    const message: Message = {
      type: 'getBuyingList',
      payload: markerInfo,
    };
    sendReactNativeMessage(message);
  };

  const closeBottomSheet = () => {
    setBottomSheetVisible(false);
    const message: Message = {
      type: 'closeBottomSheet',
    };
    sendReactNativeMessage(message);
  };

  const handleClickMarker = (markerInfo: BuyingComplex) => {
    if (markerInfo.complexId === selectedComplex?.complexId) {
      setSelectedComplex(null);
      closeBottomSheet();
    } else {
      openBottomSheet(markerInfo);
      setSelectedComplex(markerInfo);
    }
  };

  return (
    <Container>
      <NaverMap id="map">
        {!markerArray.length && <Popup />}
        {map.current &&
          markerArray?.map((markerInfo) => (
            <BuyingMapMarker
              key={markerInfo.markerId}
              map={map.current}
              markerInfo={markerInfo}
              onClickMarker={handleClickMarker}
              selectedComplex={selectedComplex}
              bottomSheetVisible={bottomSheetVisible}
            />
          ))}
      </NaverMap>
    </Container>
  );
};

export default BuyingMap;

const NaverMap = styled.div`
  width: 100%;
  height: 100%;
`;

BuyingMap.defaultProps = {
  center: { x: 33.3572421, y: 126.5322317 },
  mapTypeId: 'NORMAL',
};

const Container = styled.div`
  width: 100%;
  height: 100%;
`;
